import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import BlueprintCanvas from "@/components/BlueprintCanvas";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, PencilRuler } from "lucide-react";
import { toast } from "sonner";

const ROOM_OPTIONS = ["Kitchen", "Living Room", "Master Bedroom", "Bedroom", "Bathroom", "Garage", "Office", "Loading Bay", "Storage", "Break Room", "Reception"];
const STYLE_OPTIONS = ["Modern", "Farmhouse", "Craftsman", "Colonial", "Contemporary", "Ranch", "Industrial", "Mediterranean", "Traditional"];

export default function Builder() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ building_types: [], finish_levels: [], regions: [], countries: [] });
  const [saving, setSaving] = useState(false);
  const [blueprint, setBlueprint] = useState({ rooms: [], walls: [] });
  const [form, setForm] = useState({
    name: "",
    building_type: "single_family_home",
    square_footage: 2000,
    floors: 1,
    bedrooms: 3,
    bathrooms: 2,
    finish_level: "standard",
    region: "National Average",
    architectural_style: "Modern",
    budget: "",
    lot_size: "",
    zip_code: "",
    country: "US",
    area_unit: "sqft",
    city: "National average",
    rooms: [],
    description: "",
  });

  useEffect(() => {
    api.get("/building-types").then(({ data }) => {
      setMeta(data);
      setForm((f) => ({ ...f, building_type: data.building_types[0]?.id || f.building_type }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onCountry = (code) => {
    const c = (meta.countries || []).find((x) => x.code === code);
    const unit = c?.unit === "metric" ? "sqm" : "sqft";
    setForm((f) => ({ ...f, country: code, area_unit: unit, city: "National average" }));
  };
  const toggleRoom = (r) =>
    setForm((f) => ({ ...f, rooms: f.rooms.includes(r) ? f.rooms.filter((x) => x !== r) : [...f.rooms, r] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Give your project a name");
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        name: form.name,
        spec: { ...form, square_footage: Number(form.square_footage), floors: Number(form.floors), bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), budget: form.budget ? Number(form.budget) : null, lot_size: form.lot_size ? Number(form.lot_size) : null },
        blueprint,
      });
      toast.success("Estimate generated");
      navigate(`/projects/${data.project_id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(String(err.response.data.detail));
        navigate("/billing");
      } else {
        toast.error(err.response?.data?.detail ? String(err.response.data.detail) : "Failed to create project");
      }
    } finally {
      setSaving(false);
    }
  };

  const field = "mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">New Build</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">Configure your structure</h1>

        <form onSubmit={submit} className="mt-8 grid gap-6 lg:grid-cols-5">
          {/* form */}
          <div className="space-y-5 border border-border p-6 lg:col-span-2">
            <div>
              <Label className={lbl}>Project name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="build-name"
                placeholder="e.g. Lakeside Family Home" className={field} />
            </div>

            <div>
              <Label className={lbl}>Building type</Label>
              <Select value={form.building_type} onValueChange={(v) => set("building_type", v)}>
                <SelectTrigger data-testid="build-type" className={field}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.building_types.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={lbl}>Country</Label>
                <Select value={form.country} onValueChange={onCountry}>
                  <SelectTrigger data-testid="build-country" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {meta.countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={lbl}>Units</Label>
                <Select value={form.area_unit} onValueChange={(v) => set("area_unit", v)}>
                  <SelectTrigger data-testid="build-unit" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">Square feet</SelectItem>
                    <SelectItem value="sqm">Square metres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {((meta.cities && meta.cities[form.country]) || []).length > 1 && (
              <div>
                <Label className={lbl}>City</Label>
                <Select value={form.city} onValueChange={(v) => set("city", v)}>
                  <SelectTrigger data-testid="build-city" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {meta.cities[form.country].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={lbl}>Size ({form.area_unit === "sqm" ? "sq m" : "sq ft"})</Label>
                <Input type="number" min={10} value={form.square_footage} onChange={(e) => set("square_footage", e.target.value)}
                  data-testid="build-sqft" className={field} />
              </div>
              <div>
                <Label className={lbl}>Floors</Label>
                <Input type="number" min={1} value={form.floors} onChange={(e) => set("floors", e.target.value)}
                  data-testid="build-floors" className={field} />
              </div>
              <div>
                <Label className={lbl}>Bedrooms</Label>
                <Input type="number" min={0} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}
                  data-testid="build-bedrooms" className={field} />
              </div>
              <div>
                <Label className={lbl}>Bathrooms</Label>
                <Input type="number" min={0} value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)}
                  data-testid="build-bathrooms" className={field} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={lbl}>Finish level</Label>
                <Select value={form.finish_level} onValueChange={(v) => set("finish_level", v)}>
                  <SelectTrigger data-testid="build-finish" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.finish_levels.map((f) => (
                      <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={lbl}>Region</Label>
                <Select value={form.region} onValueChange={(v) => set("region", v)}>
                  <SelectTrigger data-testid="build-region" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.regions.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={lbl}>Architectural style</Label>
                <Select value={form.architectural_style} onValueChange={(v) => set("architectural_style", v)}>
                  <SelectTrigger data-testid="build-style" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STYLE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={lbl}>Target budget ($)</Label>
                <Input type="number" min={0} value={form.budget} onChange={(e) => set("budget", e.target.value)}
                  data-testid="build-budget" placeholder="Optional" className={field} />
              </div>
              <div>
                <Label className={lbl}>Lot size (sq ft)</Label>
                <Input type="number" min={0} value={form.lot_size} onChange={(e) => set("lot_size", e.target.value)}
                  data-testid="build-lot" placeholder="Optional" className={field} />
              </div>
              <div>
                <Label className={lbl}>ZIP code</Label>
                <Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)}
                  data-testid="build-zip" placeholder="Optional" className={field} />
              </div>
            </div>

            <div>
              <Label className={lbl}>Rooms & spaces</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ROOM_OPTIONS.map((r) => (
                  <button key={r} type="button" onClick={() => toggleRoom(r)} data-testid={`room-chip-${r}`}
                    className={`rounded-sm border px-2.5 py-1 font-mono2 text-xs transition-colors ${
                      form.rooms.includes(r) ? "border-primary bg-primary text-black" : "border-border text-muted-foreground hover:border-primary/50"
                    }`}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className={lbl}>Notes for the estimator</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                data-testid="build-notes" placeholder="Special materials, structural needs, etc." className={field} rows={3} />
            </div>

            <Button type="submit" disabled={saving} data-testid="generate-estimate-btn"
              className="w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate Estimate</>}
            </Button>
          </div>

          {/* canvas */}
          <div className="border border-border p-6 lg:col-span-3">
            <div className="mb-4 flex items-center gap-2">
              <PencilRuler className="h-4 w-4 text-primary" />
              <h2 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Blueprint Canvas (optional)</h2>
            </div>
            <BlueprintCanvas value={blueprint} onChange={setBlueprint} />
          </div>
        </form>
      </div>
    </div>
  );
}
